import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import ApiService from 'services/apiService';
import { SchoolEnrollmentSummaryDto } from 'generated/openApi';

interface SchoolEnrollmentFilter {
    schoolYear: number | null;
}

interface SchoolEnrollmentContextValue {
    // Data
    data: SchoolEnrollmentSummaryDto[];
    loading: boolean;
    error: string | null;
    
    // Filter State
    filter: SchoolEnrollmentFilter;
    updateFilter: (updates: Partial<SchoolEnrollmentFilter>) => void;
    
    // Active Year State
    activeSchoolYear: number | null;
    schoolYears: number[];
}

const SchoolEnrollmentDataContext = createContext<SchoolEnrollmentContextValue | undefined>(undefined);

export const SchoolEnrollmentDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Filter starts null to use active year from backend
    const [filter, setFilter] = useState<SchoolEnrollmentFilter>({ 
        schoolYear: null 
    });

    const [data, setData] = useState<SchoolEnrollmentSummaryDto[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await ApiService.home.getSchoolEnrollmentSummary({
                schoolYear: filter.schoolYear ?? undefined
            });

            setData(response.data || []);

            // Capture activeSchoolYear from response
            setActiveSchoolYear(prev => {
                if (prev === null && response.activeSchoolYear) {
                    return response.activeSchoolYear;
                }
                return prev;
            });

        } catch (err: any) {
            console.error('Failed to fetch school enrollment data:', err);
            setError('Failed to load enrollment data');
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [filter.schoolYear]);

    // Single effect to fetch on mount and when filter changes
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const updateFilter = useCallback((updates: Partial<SchoolEnrollmentFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    }, []);

    // Generate last 5 years based on activeSchoolYear
    const schoolYears: number[] = activeSchoolYear
        ? Array.from({ length: 5 }, (_, i) => activeSchoolYear - i)
        : [];

    return (
        <SchoolEnrollmentDataContext.Provider value={{ 
            data, 
            loading, 
            error,
            filter,
            updateFilter,
            activeSchoolYear,
            schoolYears
        }}>
            {children}
        </SchoolEnrollmentDataContext.Provider>
    );
};

export const useSchoolEnrollmentData = () => {
    const context = useContext(SchoolEnrollmentDataContext);
    if (!context) throw new Error('useSchoolEnrollmentData must be used within SchoolEnrollmentDataProvider');
    return context;
};